import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  EuroIcon,
  Gift,
  Search,
  Shield,
  ShoppingCart,
  UserPlus,
} from 'lucide-react';
import Navbar from './Navbar';
import SeoHead from './SeoHead';
import SiteFooter from './SiteFooter';
import TrustBar from './TrustBar';
import { getBrandLogo } from '../utils/brandLogos';
import { formatPrice } from '../utils/formatPrice';

const buyerSteps = [
  {
    icon: Search,
    title: 'Kies je cadeaubon',
    text: 'Bekijk het aanbod en kies een cadeaukaart met korting.',
  },
  {
    icon: CreditCard,
    title: 'Betaal veilig via iDEAL',
    text: 'Reken af met iDEAL. Je betaling blijft beschermd totdat je de bon hebt gecheckt.',
  },
  {
    icon: Gift,
    title: 'Ontvang je code',
    text: 'Na verificatie ontvang je de code per e-mail. Klaar om te gebruiken.',
  },
  {
    icon: Shield,
    title: 'Check binnen 12 uur',
    text: 'Test de code bij de winkel. Werkt alles? Dan is de koop definitief. Werkt het niet? Als het nodig is, vergoeden we je aankoop volledig.',
  },
];

const sellerSteps = [
  {
    icon: UserPlus,
    title: 'Maak een account aan',
    text: 'Meld je gratis aan en bevestig je e-mailadres.',
  },
  {
    icon: EuroIcon,
    title: 'Voeg je bon toe',
    text: 'Kies het merk, vul de waarde en kaartgegevens in en upload een foto. Bepaal zelf je verkoopprijs.',
  },
  {
    icon: Shield,
    title: 'Wij controleren je bon',
    text: 'Ons team verifieert het saldo en zet je bon live op de marktplaats.',
  },
  {
    icon: ShoppingCart,
    title: 'Ontvang je uitbetaling',
    text: 'Zodra je bon is verkocht, regelen wij je uitbetaling.',
  },
];

const faqs = [
  {
    q: 'Waarom zijn de cadeaubonnen goedkoper?',
    a: 'Andere mensen verkopen hun ongebruikte cadeaubonnen op Swapify. Omdat zij liever geld ontvangen, bieden ze hun bon aan met korting.',
  },
  {
    q: 'Is het veilig om te kopen op Swapify?',
    a: 'Ja. Elke bon wordt gecontroleerd door ons team. Je betaling blijft beschermd totdat je de code hebt gecheckt. Als het nodig is, vergoeden we je aankoop volledig.',
  },
  {
    q: 'Wat als mijn cadeaubon niet werkt?',
    a: 'Meld het binnen 12 uur via hello@swapify.nl. We onderzoeken je melding en bij een gegronde klacht krijg je een volledige terugbetaling.',
  },
  {
    q: 'Kan ik een cadeaubon retourneren?',
    a: 'Omdat het digitale codes zijn, is retourneren na ontvangst niet mogelijk.',
  },
  {
    q: 'Wat kost het om te verkopen?',
    a: 'Aanmelden is gratis. Bij een succesvolle verkoop rekenen we 4,5% platformkosten. Je ziet vooraf precies wat je ontvangt.',
  },
  {
    q: 'Hoe snel krijg ik mijn uitbetaling?',
    a: 'We maken het geld de volgende werkdag na verificatie over naar je rekening.',
  },
  {
    q: 'Welke cadeaubonnen kan ik kopen of verkopen?',
    a: 'Op dit moment werken we met een beperkt aantal merken. We breiden het aanbod stap voor stap uit. Wil je een merk laten toevoegen? Laat het ons weten via hello@swapify.nl.',
  },
  {
    q: 'Wie verwerkt mijn betaling?',
    a: 'Wij verwerken je betaling via onze betaalpartner. Zo blijft je betaling beschermd totdat je de cadeaubon hebt gecheckt.',
  },
];

const PricingCard = ({ title, icon: Icon, accentClasses, children }) => (
  <div className={`rounded-[1.75rem] border p-6 shadow-sm ${accentClasses}`}>
    <h3 className="flex items-center gap-3 text-xl font-bold text-gray-900">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-sm">
        <Icon className="h-5 w-5 text-red-500" />
      </span>
      {title}
    </h3>
    <div className="mt-6 space-y-3 text-sm text-gray-700">
      {children}
    </div>
  </div>
);

const StepSection = ({ eyebrow, title, steps, primaryLabel, primaryTo }) => (
  <section className="px-4 py-10 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-red-500">{eyebrow}</p>
        <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">{title}</h2>
      </div>
      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="rounded-[1.75rem] bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-5 inline-flex rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
                Stap {index + 1}
              </div>
              <h3 className="mt-4 text-xl font-semibold text-gray-900">{step.title}</h3>
              <p className="mt-3 text-sm leading-7 text-gray-600">{step.text}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-8">
        <Link
          to={primaryTo}
          className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-7 py-4 font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
        >
          {primaryLabel}
        </Link>
      </div>
    </div>
  </section>
);

const HowItWorksPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,202,202,0.45),_transparent_35%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_34%,_#fff1f2_100%)]">
      <SeoHead
        title="Zo werkt Swapify | Cadeaubonnen kopen en verkopen"
        description="Cadeaubonnen kopen met korting of verkopen voor geld. Veilig, simpel en zonder gedoe."
        canonicalPath="/how-it-works"
      />
      <Navbar />

      <section className="px-4 pb-12 pt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gradient-to-r from-red-500 to-orange-500 px-8 py-12 text-white shadow-xl md:px-14 md:py-16">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight md:text-6xl">Zo werkt Swapify</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-red-50">
              Cadeaubonnen kopen met korting of verkopen voor geld. Veilig, simpel en zonder gedoe.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <TrustBar />
      </section>

      <StepSection
        eyebrow="Voor Kopers"
        title="Cadeaubon kopen met korting"
        steps={buyerSteps}
        primaryLabel="Bekijk het aanbod"
        primaryTo="/marketplace"
      />

      <StepSection
        eyebrow="Voor Verkopers"
        title="Cadeaubon verkopen voor geld"
        steps={sellerSteps}
        primaryLabel="Verkoop je bon"
        primaryTo="/sell"
      />

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-red-500">Rekenvoorbeeld</p>
            <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Zo kan het eruitzien</h2>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <PricingCard title="Als koper" icon={ShoppingCart} accentClasses="border-red-100 bg-[#fff8f5]">
              <div className="flex items-center gap-3 rounded-2xl bg-white p-4">
                <img
                  src={getBrandLogo('Bol.com')}
                  alt="Bol.com"
                  width="80"
                  height="80"
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <p className="font-semibold text-gray-900">Bol.com cadeaukaart</p>
                  <p className="text-sm text-gray-500">Waarde {formatPrice(50)}</p>
                </div>
              </div>
              <div className="flex justify-between">
                <span>Jij betaalt</span>
                <span className="font-semibold text-gray-900">{formatPrice(47.03)}</span>
              </div>
              <div className="border-t border-red-200 pt-3 flex justify-between">
                <span className="font-bold text-gray-900">Je bespaart</span>
                <span className="font-bold text-red-500">{formatPrice(2.97)}</span>
              </div>
              <div className="rounded-2xl bg-red-100 px-4 py-3">
                <p className="font-semibold text-red-700">Korting op je cadeaukaart, zonder gedoe.</p>
              </div>
            </PricingCard>

            <PricingCard title="Als verkoper" icon={EuroIcon} accentClasses="border-orange-100 bg-[#fffaf3]">
              <div className="flex items-center gap-3 rounded-2xl bg-white p-4">
                <img
                  src={getBrandLogo('Bol.com')}
                  alt="Bol.com"
                  width="80"
                  height="80"
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <p className="font-semibold text-gray-900">Bol.com cadeaukaart</p>
                  <p className="text-sm text-gray-500">Waarde {formatPrice(50)}</p>
                </div>
              </div>
              <div className="flex justify-between">
                <span>Jij wilt ontvangen</span>
                <span className="font-semibold text-gray-900">{formatPrice(45)}</span>
              </div>
              <div className="flex justify-between">
                <span>Platformkosten (4,5%)</span>
                <span className="font-semibold text-gray-900">{formatPrice(2.03)}</span>
              </div>
              <div className="flex justify-between">
                <span>Koper betaalt</span>
                <span className="font-semibold text-gray-900">{formatPrice(47.03)}</span>
              </div>
              <div className="border-t border-orange-200 pt-3 flex justify-between">
                <span className="font-bold text-gray-900">Jij ontvangt</span>
                <span className="font-bold text-orange-600">{formatPrice(45)}</span>
              </div>
              <div className="rounded-2xl bg-orange-100 px-4 py-3">
                <p className="font-semibold text-orange-700">Volgende dag op je rekening!</p>
              </div>
            </PricingCard>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-red-500">Veelgestelde vragen</p>
            <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Veelgestelde vragen</h2>
          </div>
          <div className="mt-8 space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;

              return (
                <div key={faq.q} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    className="flex w-full items-center justify-between px-6 py-5 text-left transition hover:bg-gray-50"
                  >
                    <span className="pr-8 font-semibold text-gray-900">{faq.q}</span>
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5 flex-shrink-0 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-500" />
                    )}
                  </button>
                  {isOpen ? (
                    <div className="border-t border-gray-100 bg-[#fffaf5] px-6 py-5">
                      <p className="text-sm leading-7 text-gray-600">{faq.a}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-gradient-to-r from-red-500 to-orange-500 p-10 text-white shadow-xl md:p-14">
          <h2 className="text-3xl font-bold md:text-4xl">Klaar om te beginnen?</h2>
          <p className="mt-4 max-w-2xl text-base leading-7 text-red-50">
            Bekijk het aanbod of verkoop je ongebruikte cadeaubon.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/marketplace"
              className="inline-flex items-center justify-center rounded-2xl bg-white px-7 py-4 font-semibold text-red-500 transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              Bekijk het aanbod
            </Link>
            <Link
              to="/sell"
              className="inline-flex items-center justify-center rounded-2xl border border-white/70 px-7 py-4 font-semibold text-white transition hover:bg-white hover:text-red-500"
            >
              Verkoop je bon
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default HowItWorksPage;
